import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useLocation } from "react-router-dom";
import PageHeader from "../components/PageHeader";
import { getApiBase } from "../api";

export default function CarrierTracking() {
  const loc = useLocation();
  const apiBase = useMemo(() => getApiBase(), []);
  const [carrierEmail, setCarrierEmail] = useState(loc.state?.carrierEmail || "dispatch@bestcarrier.com");

  const [myLoads, setMyLoads] = useState([]);
  const [selectedLoad, setSelectedLoad] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Location state
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [gettingLocation, setGettingLocation] = useState(false);

  // Continuous tracking state
  const [isTracking, setIsTracking] = useState(false);
  const [watchId, setWatchId] = useState(null);
  const [lastSubmitTime, setLastSubmitTime] = useState(null);
  const [trackingInterval, setTrackingInterval] = useState(5); // minutes

  const loadMyLoads = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/tracking/my-loads`, {
        headers: { "x-carrier-email": carrierEmail }
      });
      if (res.ok) {
        const data = await res.json();
        setMyLoads(data.loads || []);
        if (data.loads?.length && !selectedLoad) {
          setSelectedLoad(data.loads[0].id);
        }
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [apiBase, carrierEmail, selectedLoad]);

  useEffect(() => {
    loadMyLoads();
  }, [loadMyLoads]);

  // Cleanup watch on unmount
  useEffect(() => {
    return () => {
      if (watchId) {
        navigator.geolocation.clearWatch(watchId);
      }
    };
  }, [watchId]);

  function getCurrentLocation() {
    if (!navigator.geolocation) {
      setError("Geolocation is not supported by your browser");
      return;
    }

    setGettingLocation(true);
    setError("");

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLatitude(position.coords.latitude.toFixed(6));
        setLongitude(position.coords.longitude.toFixed(6));
        setGettingLocation(false);
        setSuccess("Location captured!");
        setTimeout(() => setSuccess(""), 3000);
      },
      (error) => {
        setGettingLocation(false);
        handleLocationError(error);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  }

  function handleLocationError(error) {
    switch (error.code) {
      case error.PERMISSION_DENIED:
        setError("Location permission denied. Please enable location access in your browser settings.");
        break;
      case error.POSITION_UNAVAILABLE:
        setError("Location information unavailable.");
        break;
      case error.TIMEOUT:
        setError("Location request timed out.");
        break;
      default:
        setError("An unknown error occurred.");
    }
  }

  async function submitLocation(lat, lng, autoSubmit = false) {
    const useLat = lat || latitude;
    const useLng = lng || longitude;
    
    if (!useLat || !useLng) {
      if (!autoSubmit) setError("Please get your current location first");
      return;
    }

    if (!autoSubmit) setLoading(true);
    setError("");

    try {
      const res = await fetch(`${apiBase}/tracking/location`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-carrier-email": carrierEmail
        },
        body: JSON.stringify({
          loadId: selectedLoad || null,
          latitude: parseFloat(useLat),
          longitude: parseFloat(useLng),
          city: city || null,
          state: state || null,
          source: autoSubmit ? "AUTO_TRACK" : "MOBILE_APP"
        })
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || "Failed to submit location");
      }

      setLastSubmitTime(new Date());
      if (!autoSubmit) {
        setSuccess("Location submitted successfully!");
        setTimeout(() => setSuccess(""), 3000);
      }
    } catch (e) {
      if (!autoSubmit) setError(e.message);
      console.error("Location submit error:", e);
    } finally {
      if (!autoSubmit) setLoading(false);
    }
  }

  function startContinuousTracking() {
    if (!navigator.geolocation) {
      setError("Geolocation is not supported");
      return;
    }

    if (!selectedLoad) {
      setError("Please select a load first");
      return;
    }

    setError("");
    setIsTracking(true);

    // Use watchPosition for continuous updates
    const id = navigator.geolocation.watchPosition(
      (position) => {
        const lat = position.coords.latitude.toFixed(6);
        const lng = position.coords.longitude.toFixed(6);
        setLatitude(lat);
        setLongitude(lng);

        // Check if we should submit (based on interval)
        const now = new Date();
        const intervalMs = trackingInterval * 60 * 1000;
        
        if (!lastSubmitTime || (now - lastSubmitTime) >= intervalMs) {
          submitLocation(lat, lng, true);
        }
      },
      (error) => {
        handleLocationError(error);
        setIsTracking(false);
      },
      { 
        enableHighAccuracy: true, 
        timeout: 30000, 
        maximumAge: 0 // Always get fresh position
      }
    );

    setWatchId(id);

    // Also set up interval to ensure periodic submission
    const intervalId = setInterval(() => {
      if (latitude && longitude) {
        submitLocation(latitude, longitude, true);
      }
    }, trackingInterval * 60 * 1000);

    // Store interval ID in watchId for cleanup
    setWatchId(prev => ({ geoId: id, intervalId }));
  }

  function stopContinuousTracking() {
    if (watchId) {
      if (typeof watchId === 'object') {
        navigator.geolocation.clearWatch(watchId.geoId);
        clearInterval(watchId.intervalId);
      } else {
        navigator.geolocation.clearWatch(watchId);
      }
    }
    setWatchId(null);
    setIsTracking(false);
    setSuccess("Tracking stopped");
    setTimeout(() => setSuccess(""), 3000);
  }

  function formatTime(date) {
    if (!date) return "Never";
    return date.toLocaleTimeString();
  }

  return (
    <div style={{ padding: 24, maxWidth: 800, margin: "0 auto" }}>
      <PageHeader 
        title="📍 Share Location" 
        subtitle="Update your current location for active loads"
      />

      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 13, color: "#666" }}>Carrier Email</div>
          <input 
            value={carrierEmail} 
            onChange={(e) => setCarrierEmail(e.target.value)} 
            style={{ width: 280, padding: 8 }} 
          />
        </div>
        <button onClick={loadMyLoads} disabled={loading} style={{ padding: "10px 16px" }}>
          Refresh
        </button>
      </div>

      {error && (
        <div style={{ padding: 12, background: "#fee2e2", borderRadius: 8, marginBottom: 16, color: "#991b1b" }}>
          {error}
        </div>
      )}

      {success && (
        <div style={{ padding: 12, background: "#d1fae5", borderRadius: 8, marginBottom: 16, color: "#065f46" }}>
          {success}
        </div>
      )}

      {/* Continuous Tracking Card */}
      <div style={{ 
        background: isTracking ? "#ecfdf5" : "white", 
        borderRadius: 12, 
        border: isTracking ? "2px solid #10b981" : "1px solid #e5e7eb", 
        padding: 24, 
        marginBottom: 20 
      }}>
        <h3 style={{ margin: "0 0 16px 0", fontWeight: 700, display: "flex", alignItems: "center", gap: 8 }}>
          🛰️ Continuous Tracking
          {isTracking && (
            <span style={{ 
              padding: "4px 10px", 
              background: "#10b981", 
              color: "white", 
              borderRadius: 20, 
              fontSize: 12, 
              fontWeight: 700,
              animation: "pulse 2s infinite"
            }}>
              ● LIVE
            </span>
          )}
        </h3>

        {/* Select Load */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
            Select Load *
          </label>
          <select
            value={selectedLoad}
            onChange={(e) => setSelectedLoad(e.target.value)}
            disabled={isTracking}
            style={{ width: "100%", padding: 10, borderRadius: 6, border: "1px solid #d1d5db" }}
          >
            <option value="">-- Select a load --</option>
            {myLoads.map(load => (
              <option key={load.id} value={load.id}>
                {load.referenceNumber || load.id.slice(0, 8)} - {load.originCity || load.originAddress?.slice(0,15)}, {load.originState} → {load.destCity || load.destAddress?.slice(0,15)}, {load.destState} ({load.status})
              </option>
            ))}
          </select>
        </div>

        {/* Tracking Interval */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
            Update Interval
          </label>
          <select
            value={trackingInterval}
            onChange={(e) => setTrackingInterval(Number(e.target.value))}
            disabled={isTracking}
            style={{ width: "100%", padding: 10, borderRadius: 6, border: "1px solid #d1d5db" }}
          >
            <option value={1}>Every 1 minute</option>
            <option value={2}>Every 2 minutes</option>
            <option value={5}>Every 5 minutes</option>
            <option value={10}>Every 10 minutes</option>
            <option value={15}>Every 15 minutes</option>
            <option value={30}>Every 30 minutes</option>
          </select>
        </div>

        {/* Start/Stop Button */}
        {!isTracking ? (
          <button
            onClick={startContinuousTracking}
            disabled={!selectedLoad}
            style={{
              width: "100%",
              padding: "16px 24px",
              background: !selectedLoad ? "#d1d5db" : "#10b981",
              color: "white",
              border: "none",
              borderRadius: 8,
              fontSize: 16,
              fontWeight: 700,
              cursor: !selectedLoad ? "not-allowed" : "pointer"
            }}
          >
            🛰️ Start Live Tracking
          </button>
        ) : (
          <button
            onClick={stopContinuousTracking}
            style={{
              width: "100%",
              padding: "16px 24px",
              background: "#ef4444",
              color: "white",
              border: "none",
              borderRadius: 8,
              fontSize: 16,
              fontWeight: 700,
              cursor: "pointer"
            }}
          >
            ⏹️ Stop Tracking
          </button>
        )}

        {/* Tracking Status */}
        {isTracking && (
          <div style={{ marginTop: 16, padding: 12, background: "#d1fae5", borderRadius: 8 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: "#065f46" }}>
              📍 Current: {latitude}, {longitude}
            </div>
            <div style={{ fontSize: 13, color: "#047857", marginTop: 4 }}>
              Last submitted: {formatTime(lastSubmitTime)}
            </div>
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
              Next update in ~{trackingInterval} min | Keep this page open
            </div>
          </div>
        )}
      </div>

      {/* Manual Location Card */}
      <div style={{ background: "white", borderRadius: 12, border: "1px solid #e5e7eb", padding: 24, marginBottom: 20 }}>
        <h3 style={{ margin: "0 0 20px 0", fontWeight: 700 }}>📍 Manual Location Update</h3>

        {/* Get Location Button */}
        <button
          onClick={getCurrentLocation}
          disabled={gettingLocation || isTracking}
          style={{
            width: "100%",
            padding: "14px 24px",
            background: (gettingLocation || isTracking) ? "#9ca3af" : "#2563eb",
            color: "white",
            border: "none",
            borderRadius: 8,
            fontSize: 15,
            fontWeight: 700,
            cursor: (gettingLocation || isTracking) ? "not-allowed" : "pointer",
            marginBottom: 16
          }}
        >
          {gettingLocation ? "📍 Getting Location..." : "📍 Get Current Location"}
        </button>

        {/* Location Display */}
        {latitude && longitude && !isTracking && (
          <div style={{ background: "#f0fdf4", borderRadius: 8, padding: 16, marginBottom: 16 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              <div>
                <label style={{ fontSize: 12, color: "#6b7280" }}>Latitude</label>
                <div style={{ fontSize: 18, fontWeight: 700 }}>{latitude}</div>
              </div>
              <div>
                <label style={{ fontSize: 12, color: "#6b7280" }}>Longitude</label>
                <div style={{ fontSize: 18, fontWeight: 700 }}>{longitude}</div>
              </div>
            </div>
            
            <div style={{ marginTop: 16, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <label style={{ fontSize: 12, color: "#6b7280" }}>City (Optional)</label>
                <input
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="e.g., Dallas"
                  style={{ width: "100%", padding: 8, borderRadius: 6, border: "1px solid #d1d5db" }}
                />
              </div>
              <div>
                <label style={{ fontSize: 12, color: "#6b7280" }}>State (Optional)</label>
                <input
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  placeholder="e.g., TX"
                  style={{ width: "100%", padding: 8, borderRadius: 6, border: "1px solid #d1d5db" }}
                />
              </div>
            </div>

            <a 
              href={`https://www.google.com/maps?q=${latitude},${longitude}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ 
                display: "inline-block",
                marginTop: 12,
                fontSize: 13,
                color: "#2563eb"
              }}
            >
              🗺️ View on Google Maps
            </a>
          </div>
        )}

        {/* Submit Button */}
        <button
          onClick={() => submitLocation()}
          disabled={loading || !latitude || !longitude || isTracking}
          style={{
            width: "100%",
            padding: "14px 24px",
            background: (!latitude || !longitude || isTracking) ? "#d1d5db" : "#059669",
            color: "white",
            border: "none",
            borderRadius: 8,
            fontSize: 15,
            fontWeight: 700,
            cursor: (!latitude || !longitude || isTracking) ? "not-allowed" : "pointer"
          }}
        >
          {loading ? "Submitting..." : "✅ Submit Location Update"}
        </button>
      </div>

      {/* Active Loads */}
      <div style={{ background: "white", borderRadius: 12, border: "1px solid #e5e7eb" }}>
        <div style={{ padding: 16, borderBottom: "1px solid #e5e7eb" }}>
          <h3 style={{ margin: 0, fontWeight: 700 }}>🚛 Your Active Loads ({myLoads.length})</h3>
        </div>
        
        {myLoads.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", color: "#6b7280" }}>
            No active loads assigned to you
          </div>
        ) : (
          <div>
            {myLoads.map(load => (
              <div 
                key={load.id} 
                onClick={() => !isTracking && setSelectedLoad(load.id)}
                style={{ 
                  padding: 16, 
                  borderBottom: "1px solid #e5e7eb",
                  cursor: isTracking ? "default" : "pointer",
                  background: selectedLoad === load.id ? "#eff6ff" : "white"
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
                  <span style={{
                    padding: "3px 8px",
                    borderRadius: 4,
                    fontSize: 11,
                    fontWeight: 700,
                    background: load.status === 'PICKED_UP' ? '#8b5cf6' : '#3b82f6',
                    color: "white"
                  }}>
                    {load.status}
                  </span>
                  <span style={{ fontWeight: 600 }}>{load.referenceNumber || load.id.slice(0, 8)}</span>
                  {selectedLoad === load.id && (
                    <span style={{ marginLeft: "auto", fontSize: 12, color: "#10b981" }}>
                      ✓ Selected
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 14, color: "#374151" }}>
                  {load.originCity || load.originAddress?.slice(0,20)}, {load.originState} → {load.destCity || load.destAddress?.slice(0,20)}, {load.destState}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* PWA Install Hint */}
      <div style={{ marginTop: 20, padding: 16, background: "#fef3c7", borderRadius: 8, fontSize: 13 }}>
        <strong>💡 Tip:</strong> Add this page to your home screen for easy access! On iPhone: tap Share → "Add to Home Screen". On Android: tap Menu → "Add to Home screen".
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </div>
  );
}
